import type { SheetSnapHeights } from './types';
import type { ComputedRef, Ref } from 'vue';

import { computed } from 'vue';

import { SNAP_LEVELS } from './constants';
import { SheetLevel } from './types';

// Calcula as posicoes de snap do drawer em coordenada virtual.
// Este arquivo nao toca DOM: apenas regras de geometria.
export interface SheetGeometry {
	maxOffset: ComputedRef<number>;
	spacerExtent: ComputedRef<number>;
	snapScrollY: ComputedRef<Record<SheetLevel, number>>;
	peek: ComputedRef<number>;
	levelAt: (y: number) => SheetLevel;
}

export function useSheetGeometry(
	snapHeights: Ref<SheetSnapHeights>,
	viewportHeight: Ref<number>,
	topChromeHeight: number,
	closedTopChromeHeight: number = topChromeHeight
): SheetGeometry {
	// Altura visivel no estado de peek.
	const peek = computed(() => snapHeights.value[2]);
	// Altura visivel no estado intermediario.
	const mid = computed(() => snapHeights.value[1]);

	// Extensao do spacer acima do sheet. Junto com o padding-top da pagina
	// (= topChromeHeight), ancora o peek no rodape da viewport:
	// padding + spacer = viewport - peek. NAO muda com a navbar.
	const spacerExtent = computed(() =>
		Math.max(0, viewportHeight.value - topChromeHeight - peek.value)
	);

	// Limite maximo virtual da faixa do drawer (estado CLOSED). Usa o chrome
	// visivel no CLOSED (so a barra de filtros, navbar oculta), entao o sheet
	// sobe alem do spacer e encosta logo abaixo dela.
	const maxOffset = computed(() =>
		Math.max(0, viewportHeight.value - closedTopChromeHeight - peek.value)
	);

	// Tabela de snaps virtuais por nivel. O half-open e limitado a maxOffset para
	// garantir a ordem ascendente FULLY_OPEN <= HALF_OPEN <= CLOSED (de que o resto
	// do modulo depende), mesmo com snapHeights atipicos.
	const snapScrollY = computed(() => ({
		[SheetLevel.FULLY_OPEN]: 0,
		[SheetLevel.HALF_OPEN]: Math.min(
			Math.max(0, mid.value - peek.value),
			maxOffset.value
		),
		[SheetLevel.CLOSED]: maxOffset.value
	}));

	// Retorna o nivel mais proximo para um Y virtual.
	function levelAt(y: number): SheetLevel {
		const snaps = snapScrollY.value;
		return SNAP_LEVELS.reduce<SheetLevel>(
			(best, lvl) =>
				Math.abs(y - snaps[lvl]) < Math.abs(y - snaps[best]) ? lvl : best,
			SheetLevel.CLOSED
		);
	}

	return { maxOffset, spacerExtent, snapScrollY, peek, levelAt };
}
